import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { HelpButton } from '../components/HelpButton.js';
import { PageTitle } from '../components/Layouts.js';
import { SetupPanel } from '../components/SetupPanel.js';
import { UptimeChart } from '../components/UptimeChart.js';
import { Sparkline } from '../components/charts.js';
import { FullRescan } from '../components/FullRescan.js';
import { ImprovementItem, useImprovements } from '../components/Improvements.js';
import {
  GradeBadge,
  GradeScale,
  SiteAddress,
  TileStatusIcon,
  useLocale,
} from '../components/site.js';
import {
  Card,
  LinkButton,
  LoadError,
  Loading,
  Notice,
  SeverityBadge,
  StatusBadge,
  UrgencyBadge,
  type Severity,
} from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDateTime } from '../i18n.js';
import { useMe } from '../session.js';

type Range = '24h' | '7d' | '30d' | '90d' | '365d';
const RANGES: Range[] = ['24h', '7d', '30d', '90d', '365d'];
/** S3: Health Score, seven tiles, "what matters now" and the uptime chart. */
export function SiteDashboard() {
  const { siteId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const locale = useLocale();
  const location = useLocation();
  const me = useMe();
  const timeZone = me.data?.account.timezone;
  const [range, setRange] = useState<Range>('24h');
  const [locked, setLocked] = useState(false);
  const overview = useQuery({
    queryKey: ['site', siteId, 'overview', locale],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/overview', {
          params: { path: { siteId }, query: { locale } },
        }),
      ),
    refetchInterval: 30_000,
  });
  const uptime = useQuery({
    queryKey: ['site', siteId, 'uptime', range],
    queryFn: () =>
      unwrap(api.GET('/sites/{siteId}/uptime', { params: { path: { siteId }, query: { range } } })),
    refetchInterval: 60_000,
  });

  // Ranges the plan shows (v2 §9); the others lead to the plans.
  const allowed: Range[] = uptime.data?.allowedRanges ?? ['24h', '7d'];

  if (overview.isPending) return <Loading />;
  if (overview.isError)
    return (
      <LoadError
        message={errorMessage(overview.error, t)}
        onRetry={() => void overview.refetch()}
      />
    );
  const o = overview.data;
  const incident = o.openIncident;
  const unreachable = (location.state as { unreachable?: boolean } | null)?.unreachable;
  const trend = o.health?.trend7d ?? null;

  return (
    <>
      <PageTitle
        actions={
          <StatusBadge
            state={
              o.site.status === 'paused'
                ? 'paused'
                : o.access.block === 'bot_protection'
                  ? 'setup'
                  : o.availability.state
            }
          />
        }
      >
        {o.site.name}
      </PageTitle>
      <p className="-mt-4 mb-4">
        <SiteAddress url={o.site.url}>{o.site.url}</SiteAddress>
      </p>

      <div className="mb-6 flex flex-col gap-3 empty:hidden">
        <SetupPanel
          siteId={siteId}
          access={o.access}
          lastCheckAt={o.availability.lastCheckAt}
          timeZone={timeZone}
          sslDaysLeft={o.security.ssl?.daysLeft ?? null}
        />
        {unreachable ? <Notice kind="warning">{t('addSite.unreachable')}</Notice> : null}
        {incident && incident.kind === 'downtime' ? (
          <Notice kind="error">
            {t('site.openIncident', {
              time: formatDateTime(incident.startedAt, i18n.language, timeZone),
            })}
          </Notice>
        ) : null}
        {o.availability.lastCheckAt ? null : <Notice kind="info">{t('site.notChecked')}</Notice>}
      </div>

      <Card className="mb-6 grid gap-6 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
        <div>
          <h2 className="mb-3 text-xl font-semibold">{t('health.title')}</h2>
          {o.health ? (
            <div className="flex flex-wrap items-center gap-4">
              <GradeBadge grade={o.health.grade} score={o.health.score} />
              <div>
                <p className="text-lg font-semibold">{o.health.label}</p>
                {trend !== null ? (
                  <p
                    className={`text-sm font-medium ${
                      trend > 0 ? 'text-green-800' : trend < 0 ? 'text-red-800' : 'text-slate-700'
                    }`}
                  >
                    {trend === 0
                      ? t('health.trendSame')
                      : t(trend > 0 ? 'health.trendUp' : 'health.trendDown', {
                          points: Math.abs(trend),
                        })}
                    {o.health.previousGrade
                      ? ` · ${t('health.previousGrade', { grade: o.health.previousGrade })}`
                      : null}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
          {o.health ? (
            <div className="mt-3">
              <GradeScale grade={o.health.grade} />
            </div>
          ) : null}
          {o.health?.nextGrade ? (
            <p className="mt-3 max-w-prose">
              {t('health.nextSteps', {
                grade: o.health.nextGrade.grade,
                count: o.health.nextGrade.steps.length,
                list: o.health.nextGrade.steps.map((x) => x.title).join(', '),
              })}
            </p>
          ) : null}
          {o.health ? null : (
            <p className="text-slate-700">
              {t(o.access.block === 'bot_protection' ? 'health.hidden' : 'health.pending')}
            </p>
          )}
        </div>
        <FullRescan
          siteId={siteId}
          panel={{
            aside: (
              <LinkButton
                to={`/sites/${siteId}/report`}
                variant="secondary"
                className="whitespace-nowrap"
              >
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-5 w-5"
                >
                  <path d="M12 3v12" />
                  <path d="m7 10 5 5 5-5" />
                  <path d="M5 21h14" />
                </svg>
                {t('report.open')}
              </LinkButton>
            ),
            lastCheck: o.availability.lastCheckAt
              ? t('rescan.lastCheck', {
                  time: formatDateTime(o.availability.lastCheckAt, i18n.language, timeZone),
                })
              : null,
          }}
        />
      </Card>

      <section aria-labelledby="top-title" className="mb-6">
        <h2 id="top-title" className="mb-3 text-xl font-semibold">
          {t('site.top.title')}
        </h2>
        {o.topFindings.length === 0 ? (
          incident && incident.kind === 'downtime' ? (
            <Notice kind="warning">{t('site.top.down')}</Notice>
          ) : (
            <Notice kind="success">{t('site.top.empty')}</Notice>
          )
        ) : (
          <ol className="flex flex-col gap-3">
            {o.topFindings.map((f, i) => (
              <li key={f.id}>
                <Card className="flex flex-col gap-3">
                  <div className="flex items-start gap-3">
                    <span
                      aria-hidden="true"
                      className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-slate-100 font-semibold text-slate-900"
                    >
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-lg font-semibold">{f.title}</h3>
                      <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-700">
                        <span>{t('site.top.priority')}</span>
                        <UrgencyBadge urgency={f.urgency} />
                        {f.places > 1 ? (
                          <span>· {t('findings.count', { count: f.places })}</span>
                        ) : null}
                      </p>
                      {f.summary ? <p className="mt-2 max-w-prose">{f.summary}</p> : null}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:pl-11">
                    <LinkButton
                      to={`/sites/${siteId}/findings?open=${encodeURIComponent(f.ruleCode)}`}
                      size="sm"
                      variant={i === 0 ? 'primary' : 'secondary'}
                      aria-label={`${t('site.top.view')}: ${f.title}`}
                    >
                      {t('site.top.view')}
                    </LinkButton>
                    {f.service ? (
                      <HelpButton
                        siteId={siteId}
                        service={f.service}
                        findingId={f.id}
                        ruleCode={f.ruleCode}
                      />
                    ) : null}
                  </div>
                </Card>
              </li>
            ))}
          </ol>
        )}
        {o.topMore > 0 ? (
          <p className="mt-3">
            <LinkButton to={`/sites/${siteId}/findings`} variant="soft" size="sm">
              {t('site.top.more', { count: o.topMore })}
            </LinkButton>
          </p>
        ) : null}
      </section>

      <WeekChanges siteId={siteId} timeZone={timeZone} news={o.newThisWeek} />

      <section aria-labelledby="tiles-title" className="mb-6">
        <h2 id="tiles-title" className="sr-only">
          {t('site.tiles.label')}
        </h2>
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {o.tiles.map((tile) => (
            <li key={tile.key}>
              <Card className="flex h-full flex-col">
                <h3 className="mb-2 text-lg font-semibold">{t(`site.tiles.${tile.key}`)}</h3>
                <div className="flex items-center justify-between gap-2">
                  <TileStatusIcon status={tile.status} />
                  <Sparkline values={tile.trend} />
                </div>
                <p className="mt-2 font-medium">{tile.headline}</p>
                {tile.sub ? <p className="mt-1 text-sm text-slate-700">{tile.sub}</p> : null}
                <p className="mt-2 text-sm text-slate-700">{tile.why}</p>
                <p className="mt-auto pt-2">
                  <LinkButton to={`/sites/${siteId}/area/${tile.key}`} variant="soft" size="sm">
                    {t('site.tiles.more')}
                  </LinkButton>
                </p>
              </Card>
            </li>
          ))}
        </ul>
      </section>

      <Card>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-xl font-semibold">{t('site.chart.title')}</h2>
          <div role="group" aria-label={t('site.chart.range')} className="flex flex-wrap gap-1">
            {RANGES.map((r) => (
              <button
                key={r}
                type="button"
                aria-pressed={range === r}
                onClick={() => {
                  if (allowed.includes(r)) {
                    setRange(r);
                    setLocked(false);
                  } else setLocked(true);
                }}
                className={`min-h-11 rounded-md border px-3 font-semibold ${range === r ? 'border-blue-700 bg-blue-700 text-white' : allowed.includes(r) ? 'border-slate-300 bg-white text-slate-900' : 'border-dashed border-slate-300 bg-slate-50 text-slate-600'}`}
              >
                {t(`site.chart.ranges.${r}`)}
                {allowed.includes(r) ? null : (
                  <span className="sr-only"> ({t('site.chart.lockedShort')})</span>
                )}
              </button>
            ))}
          </div>
        </div>
        {uptime.isPending ? <Loading /> : null}
        {uptime.isError ? (
          <LoadError
            message={errorMessage(uptime.error, t)}
            onRetry={() => void uptime.refetch()}
          />
        ) : null}
        {uptime.data ? (
          <UptimeChart
            points={uptime.data.series}
            uptimePct={uptime.data.uptimePct}
            timeZone={timeZone}
          />
        ) : null}
        {locked ? (
          <div className="mt-3">
            <Notice kind="info">
              <p>{t('site.chart.locked')}</p>
              <LinkButton to="/billing" variant="secondary" size="sm" className="mt-2">
                {t('site.chart.lockedCta')}
              </LinkButton>
            </Notice>
          </div>
        ) : null}
        {range === '30d' || range === '90d' || range === '365d' ? (
          <p className="mt-2 text-sm text-slate-700">{t('site.chart.retention')}</p>
        ) : null}
        <p className="mt-4">
          <LinkButton to={`/sites/${siteId}/incidents`} variant="soft">
            {t('site.incidentsLink')}
          </LinkButton>
        </p>
      </Card>
    </>
  );
}

/**
 * "Was sich diese Woche geändert hat" (T-36/T-40, dashboard mockup): the good news with what they
 * bring, and the problems that are new this week. Hidden while there is neither.
 */
function WeekChanges({
  siteId,
  timeZone,
  news,
}: {
  siteId: string;
  timeZone?: string | undefined;
  news: readonly { ruleCode: string; title: string; severity: Severity }[];
}) {
  const { t } = useTranslation();
  const feed = useImprovements(siteId, 7);
  const items = feed.data?.items ?? [];
  if (items.length === 0 && news.length === 0) return null;
  return (
    <section aria-labelledby="week-title" className="mb-6">
      <h2 id="week-title" className="mb-3 text-xl font-semibold">
        {t('changes.weekChanged')}
      </h2>
      <ul className="flex flex-col gap-2">
        {items.slice(0, 3).map((item) => (
          <li key={`${item.kind}-${item.at}-${item.title}`}>
            <ImprovementItem item={item} timeZone={timeZone} />
          </li>
        ))}
        {news.map((n) => (
          <li key={n.ruleCode}>
            <article className="flex items-center gap-3 rounded-md border border-amber-200 bg-white p-4">
              <span
                aria-hidden="true"
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 font-bold text-amber-900"
              >
                !
              </span>
              <p className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{t('changes.newLabel')}</span>
                <LinkButton
                  to={`/sites/${siteId}/findings?open=${encodeURIComponent(n.ruleCode)}`}
                  variant="soft"
                  size="sm"
                >
                  {n.title}
                </LinkButton>
                <SeverityBadge severity={n.severity} />
              </p>
            </article>
          </li>
        ))}
      </ul>
      <p className="mt-3">
        <LinkButton to={`/sites/${siteId}/changes`} variant="soft">
          {t('changes.all')}
        </LinkButton>
      </p>
    </section>
  );
}
